import { Component, Inject, inject, Optional, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FormFieldsSelectDataService } from '../../shared/form-fields-select-data.service';
import { filter, forkJoin, mapTo, merge, Observable, share, takeUntil, timeout, withLatestFrom } from 'rxjs';
import { DynamicFormsV2Component } from '../../shared/dynamic-forms-v2/dynamic-forms-v2.component';
import { formMetaData, FormFields } from '../../shared/dynamic-forms.interface2';
import {
  CodeableConceptField, CodeField, GroupField, IndividualField,
  codeableConceptDataType, ReferenceDataType, codingDataType,
  IndividualReferenceField, ReferenceFieldArray,
  SingleCodeField
} from '../../shared/dynamic-forms.interface2';
import { ErrorService } from '../../shared/error.service';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { commonImports } from '../../shared/table-interface';
import { CommonModule } from '@angular/common';
import { MatSelectModule } from '@angular/material/select';
import { backendEndPointToken } from '../../app.config';
import { HttpClient, HttpHeaders, HttpContext } from '@angular/common/http';
import { ResourceDataReviewComponent } from '../../shared/resource-data-review/resource-data-review.component';
import { StateService } from '../../shared/state.service';
import { LoaderComponent } from '../../loader/loader.component';
import { UtilityService } from '../../shared/utility.service';

// standalone autocomplete section
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { EmphasisDirective } from '../../shared/directives/emphasis.directive';
import { WhoicdtokenService } from '../../whoicdtoken.service';
import { DisableCountParam, DisableSortParam, LoadingUIEnabled } from '../../loading.interceptor';
import { BehaviorSubject, catchError, combineLatest, debounceTime, distinctUntilChanged, map, of, shareReplay, startWith, switchMap, tap, timer } from 'rxjs';
import { MedicineSearchDialogComponent, MedicineSearchResult } from '../medicine-search-dialog/medicine-search-dialog.component';
import { MedicationRequest } from 'fhir/r4';
import { MedicineRequestsDialogService } from '../medicine-requests-dialog.service';
import { CodeableConcept, Dosage, Reference } from 'fhir/r4';
import { NotificationService } from '../../services/notification.service';
import { AuthService } from '../../shared/auth/auth.service';
import { FirebaseAlertService } from '../../shared/services/firebase-alert.service';
import { ResourceRefreshService } from '../../shared/resource-refresh.service';

import { take } from 'rxjs/operators';


type AddMedicineRequestsDialogData = {
  isEditMode?: boolean;
  // in edit mode, pass an existing MedicationRequest
  medicationRequest?: MedicationRequest | null;
  // optional override: provide fields/meta instead of fetching in ngOnInit
  formFields?: any[] | null;
  formMetaData?: any | null;
};

// IMPORTANT: keep rxjs imports in one place
@Component({
  selector: 'app-add-medicine-requests',
  imports: [
    ...commonImports,
    CommonModule,
    ResourceDataReviewComponent,
    MatSelectModule,
    DynamicFormsV2Component,
    MatAutocompleteModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatIconModule,
    EmphasisDirective
  ],
  templateUrl: './add-medicine-requests.component.html',
  styleUrl: './add-medicine-requests.component.scss'
})
export class AddMedicineRequestsComponent {
  medReqDialog = inject(MedicineRequestsDialogService)
  errorService = inject(ErrorService)
  dialog = inject(MatDialog);
  formFieldsDataService = inject(FormFieldsSelectDataService)
  private notificationService = inject(NotificationService);
  private auth = inject(AuthService);
  private firebaseAlertService = inject(FirebaseAlertService);
  private resourceRefresh = inject(ResourceRefreshService);
  isChosenOption = false;
  isMultiple = new FormControl(null);
  formMetaData?: formMetaData;
  formFields?: FormFields[];

  medicineForms: { formFields: FormFields[], formMetaData: formMetaData }[] = [

  ]

  private getCurrentOrganizationId(): string | null {
    return this.auth.user.getValue()?.organizationId ?? null;
  }
  public editingMedicationRequest: MedicationRequest | null = null;
  public isEditMode = false;
  // editingSubmittedData: any;
  constructor(
    public loadingRef: MatDialogRef<LoaderComponent>,
    @Inject(MAT_DIALOG_DATA) public dialogData: AddMedicineRequestsDialogData,
    @Optional() public dialogRef: MatDialogRef<AddMedicineRequestsComponent>,
    @Inject(backendEndPointToken) public backendEndPoint: string) {
    //editmode, medRequest
    this.isEditMode = !!this.dialogData?.isEditMode;
    this.editingMedicationRequest = this.dialogData?.medicationRequest ?? null;
    if (this.editingMedicationRequest) {
      this.submittedData = this.medicationRequestToDynamicFormValues(this.editingMedicationRequest);
    }


  }
  http = inject(HttpClient);

  private fb = inject(FormBuilder);

  // Root form with a form array that mirrors fields in this.formFields
  requestForm: FormGroup = this.fb.group({
    items: this.fb.array([])
  });

  get items(): FormArray {
    return this.requestForm.get('items') as FormArray;
  }

  // Builds one form group whose controls match this.formFields by fieldApiName
  private buildItemGroup(overrideValues?: Record<string, any>): FormGroup {
    const controls: Record<string, FormControl> = {};
    (this.formFields || []).forEach(f => {
      const apiName = f.generalProperties.fieldApiName;
      let initial: any;
      if (overrideValues && Object.prototype.hasOwnProperty.call(overrideValues, apiName)) {
        initial = overrideValues[apiName]; // highest precedence
      } else if ('value' in f.generalProperties) {
        initial = (f as any).generalProperties.value;
      } else {
        initial = null;
      }
      controls[apiName] = new FormControl(initial);
    });
    return this.fb.group(controls);
  }

  removeRequest(index: number) {
    this.items.removeAt(index);
  }

  // Sync single field edits from ResourceDataReviewComponent
  onFieldEdited(index: number, evt: { fieldApiName: string; newValue: any; isArray?: boolean; arrayIndex?: number }) {
    const group = this.items.at(index) as FormGroup;
    if (!group) return;

    group.patchValue({ [evt.fieldApiName]: evt.newValue });

    // keep toSubmitFinally in sync for your current submit flow
    this.toSubmitFinally = this.items.value;
  }

  // Sync full resource object updates
  onResourceUpdated(index: number, updated: any) {
    const group = this.items.at(index) as FormGroup;
    if (!group) return;
    group.patchValue(updated);
  }
  stateService = inject(StateService);
  // loadingRef = MatDialogRef<LoaderComponent>
  // constructor() {}
  // loadingRef2 = this.dialog.open(LoaderComponent);
  utilityService = inject(UtilityService)
  @ViewChild('cref') dynamicForms?: DynamicFormsV2Component;

  private whoToken = inject(WhoicdtokenService);

  // ---- ICD-11 medication search (separate section) ----
  icd11MedicationCtrl = new FormControl<string>('');
  icd11Searching$ = new BehaviorSubject<boolean>(false);

  private icd11Term$ = this.icd11MedicationCtrl.valueChanges.pipe(
    startWith(''),
    map(v => (v ?? '').toString().trim()),
    debounceTime(250),
    distinctUntilChanged()
  );
  toSubmitFinally: any;
  private stockMedicationOptions$ = new BehaviorSubject<Array<{
    reference: string; display: string; inStock: boolean,
    ingredients: string
  }>>([]);

  /**
   * Merged list: Stock meds first (with in-stock badge) + WHO ICD-11 matches
   * NOTE: using a union-like shape for rendering in template.
   */
  icd11MergedOptions$ = combineLatest([
    this.icd11Term$,
    this.stockMedicationOptions$.asObservable()
  ]).pipe(
    // background "every ~1s" search while there's a term
    switchMap(([term, stock]) => {
      const cleaned = term.trim();
      if (!cleaned) return of({ stock, who: [] as any[] });

      return timer(0, 1000).pipe(
        // keep polling for same term; if term changes combineLatest re-enters switchMap, cancelling old timer
        tap(() => this.icd11Searching$.next(true)),
        switchMap(() => this.searchWhoIcd11(cleaned).pipe(catchError(() => of([])))),
        map((who) => ({ stock, who })),
        tap(() => this.icd11Searching$.next(false))
      );
    }),
    map(({ stock, who }) => {
      const stockItems = (stock || []).map((s) => ({
        kind: 'stock' as const,
        reference: s.reference,
        label: s.display,
        inStock: s.inStock
      }));
      const whoItems = (who || []).map((r: any) => ({
        kind: 'who' as const,
        id: r?.id,
        title: r?.title,
        label: r?.title ?? r?.theCode ?? 'Unknown'
      }));

      const seen = new Set<string>();
      return [...stockItems, ...whoItems].filter((x: any) => {
        const key = x.kind === 'stock' ? x.reference : `who:${x.id ?? x.label}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  // Used by mat-autocomplete displayWith
  displayIcd11Option = (opt: any) => opt?.label ?? '';

  // When user picks an option, persist into the dynamic form value shape you already submit
  onIcd11MedicationSelected(opt: any) {
    if (!opt) return;

    // Patch the live DynamicForms form if present (single-mode UX)
    const aForm = this.dynamicForms?.aForm;

    // Build patch for nested 'medicine' group
    const patch =
      opt.kind === 'stock'
        ? { medicine: { medicationReference: opt.reference, medicationCodeableConcept: null } }
        : opt.kind === 'who'
          ? { medicine: { medicationCodeableConcept: opt.title, medicationReference: null } }
          : null;

    if (!patch) return;

    if (aForm) {
      aForm.patchValue(patch);
    } else if (this.items?.length) {
      // fallback for multiple mode if first item already exists
      (this.items.at(0) as FormGroup)?.patchValue(patch as any);
    }

    // clear the search box after selection
    this.icd11MedicationCtrl.setValue('', { emitEvent: false });
  }

  private searchWhoIcd11(q: string) {
    const url = `https://id.who.int/icd/release/11/2025-01/mms/search?q=${encodeURIComponent(q)}`;
    return this.whoToken.token$.pipe(
      switchMap((token) => {
        if (!token) return of([]);
        const headers = new HttpHeaders({
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
          'Accept-Language': 'en'
        });
        return this.http.get<any>(url, { headers });
      }),
      map((res: any) => {
        const entities = res?.destinationEntities ?? res?.entities ?? res?.results ?? [];
        const list = Array.isArray(entities) ? entities : [];
        return list.map((e: any) => ({
          id: e?.id ?? e?.['@id'] ?? e?.theCode ?? e?.code,
          title: e?.title?.['@value'] ?? e?.title ?? e?.matchingText ?? e?.theCode ?? e?.code
        }));
      })
    );
  }

  /**
   * Medication term (RxNorm) - this is what should drive medication options.
   * (Your icd11MedicationCtrl remains for ICD-11 if you still want it elsewhere.)
   */
  rxNormMedicationCtrl = new FormControl<any>('' as any);
  rxNormSearching$ = new BehaviorSubject<boolean>(false);

  private rxNormTerm$ = this.rxNormMedicationCtrl.valueChanges.pipe(
    startWith(''),
    map(v => (v ?? '').toString().trim()),

    distinctUntilChanged(),
    share()
  );

  private rxNormCachedOptions$ = new BehaviorSubject<Array<{ code: string; display: string; system: string }>>([]);

  private mergeRxNormCache(next: Array<{ code: string; display: string; system: string }>) {
    if (!next?.length) return;

    const prev = this.rxNormCachedOptions$.getValue() || [];
    const seen = new Set(prev.map(p => `${p.system}:${p.code}`));

    const merged = [...prev];
    for (const n of next) {
      const key = `${n.system}:${n.code}`;
      if (seen.has(key)) continue;
      seen.add(key);
      merged.push(n);
    }
    this.rxNormCachedOptions$.next(merged);
  }

  /**
   * RxNorm + Stock merged options:
   * - RxNorm results are cached (not cleared) until component closes
   * - Results shown are filtered by current rxNormTerm$ (autocomplete behavior)
   */
  rxNormMergedMedicationOptions$ = combineLatest([
    this.rxNormTerm$,
    this.stockMedicationOptions$.asObservable(),

    // this.rxNormCachedOptions$.asObservable()
  ]).pipe(
    debounceTime(250),
    switchMap(([term, stock]) => {

      const cleaned = (term ?? '').trim();
      console.log(term, stock);
      // No term: show only stock (and do not spam RxNorm search)
      if (!cleaned) {
        return of({ term: '', stock, rx: this.rxNormCachedOptions$.getValue() || [] });
      } else {
        this.rxNormCachedOptions$.next([]);
// $#$        // /* alert('searching'); */
        this.rxNormSearching$.next(true);
      }

      // Poll RxNorm for the current term, but keep prior results in cache
      // return timer(0, 1000).pipe(
      //   tap(() => this.rxNormSearching$.next(true)),
      // switchMap(() =>
      if (term.length < 2) {
        this.rxNormSearching$.next(false);
        return of({ term: cleaned, stock, rx: this.rxNormCachedOptions$.getValue() || [] });
      }
      return this.searchRxNormSBD(cleaned).pipe(
        tap((fresh) => this.mergeRxNormCache(fresh)),
        catchError(() => of([] as Array<{ code: string; display: string; system: string }>)),
        map(() => ({ term: cleaned, stock, rx: this.rxNormCachedOptions$.getValue() || [] })),
        // )
      )

    }),
    // after fetch attempt, use the latest cache snapshot for filtering

    tap(() => { this.rxNormSearching$.next(false); }),
    // );
    // }),
    map(({ term, stock, rx }) => {
      console.log(term, stock, rx);
      // Filter stock too (stock list can be large)
      const t = (term ?? '').toLowerCase();

      const filteredStock = !t
        ? (stock || [])
        : (stock || []).filter((s) => {
          const hay = `${s.display ?? ''} ${s.reference ?? ''}`.toLowerCase();
          return hay.includes(t);
        });

      const stockItems = filteredStock.map((s) => ({
        kind: 'stock' as const,
        reference: s.reference,
        label: <{
          display: string,
          inStock?: boolean,
          ingredients?: string
        }>{
            display: s.display,
            inStock: s.inStock,
            ingredients: s.ingredients
          },
        display: s.display.replace(/\s*\(stock:\s*[^)]*\)\s*$/i, '') + s.ingredients,
        inStock: s.inStock,
        ingredients: s.ingredients
      }));

      const filteredRx = !t
        ? (rx || [])
        : (rx || []);

      const rxItems = filteredRx.map((c: any) => {
        // include ingredient info in label (dropdown display)
        const ingredients = Array.isArray(c.ingredients) ? c.ingredients : [];
        // const ingredientsLabel = ingredients.length ? `(Ingredients: ${ingredients.join(', ')})` : '';
        const ingredientsLabel = (c.ingredients?.length) ? `${c.display ? '(Ingredients:' : ''} ${c.ingredients.join(', ')} ${c.display ? ')' : ''}` : '';

        return {
          kind: 'rxnorm' as const,
          code: c.code,
          display: c.display + " " + ingredientsLabel,
          system: c.system,
          ingredients,
          label: <{
            display: string,
            inStock?: boolean,
            ingredients?: string
          }>{
              display: c.display,
              ingredients: ingredientsLabel
            }
        };
      });

      const seen = new Set<string>();
      return [...stockItems, ...rxItems, ...(term.trim().length > 1 && !term.includes('object Object') ? [{
        kind: 'userExactWords' as const,
        code: term,
        display: term,
        system: 'user/input',
        label: <{
          display: string,
          inStock?: boolean,
          ingredients?: string
        }>{
            display: term
          }
      }] : [])].filter((x: any) => {
        const key = x.kind === 'stock' ? x.reference : `rx:${x.system}:${x.code}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );


  //determining loader from rxNormSearching$. and rxNormTerm$
  loading$ = this.rxNormTerm$.pipe(
    switchMap(() =>
      this.rxNormSearching$.pipe(
        switchMap(inFlight => {
          if (!inFlight) {
            // Hide loader immediately if request finished
            return of(false);
          }
          // Wait a bit before showing loader
          return timer(250).pipe(
            mapTo(true),
            takeUntil(
              merge(
                this.rxNormTerm$, // hide loader if user types again
                this.rxNormSearching$.pipe(filter(active => !active)) // or if request finishes
              )
            )
          );
        })
      )
    ),
    startWith(false)
  );

  // displayRxNormOption = (opt: any) => opt?.label ?? '';
  displayRxNormOption = (opt: any) => opt.display;
  // this.stripStockSuffix(opt?.label ?? '');


  private stripStockSuffix(label: string): string {
    // Removes trailing " (Stock: ...)" which you append in stock med display strings
    return (label ?? '').toString().replace(/\s*\(stock:\s*[^)]*\)\s*$/i, '').replace(/^use\s\"(.*)\"$/i, '$1').trim();
  }


  openMedicineSearchDialog() {
    const stock = this.stockMedicationOptions$.getValue() || [];
    console.log(stock);
    const dref = this.dialog.open(MedicineSearchDialogComponent, {
      width: '720px',
      maxHeight: '90vh',
      autoFocus: false,
      data: { stock }
    });

    dref.afterClosed().subscribe((opt: MedicineSearchResult | undefined) => {
      if (!opt) return;

      const aForm = this.dynamicForms?.aForm;

      const patch =
        opt.kind === 'stock'
          ? { medicine: { medicationReference: opt.reference, medicationCodeableConcept: null } }
          : opt.kind === 'rxnorm'
            ? {
              medicine: {
                medicationCodeableConcept: { code: opt.code, display: opt.label, system: opt.system },
                medicationReference: null
              }
            }
            : opt.kind === 'userExactWords'
              ? {
                medicine: {
                  medicationCodeableConcept: { text: opt.display },
                  medicationReference: null
                }
              }
              : null;

      if (!patch) return;

      if (aForm) {
        aForm.patchValue(patch);
      } else if (this.items?.length) {
        (this.items.at(0) as FormGroup)?.patchValue(patch as any);
      }
    });
  }

  private isRxNormPackOrKit(display: string): boolean {
    const s = (display ?? '').toString();
    // RxNorm pack patterns commonly include "{ ... } Pack" or any " Pack" keyword
    // These represent multiple distinct drug products bundled together.
    return s.includes('{') || /\bPack\b/i.test(s);
  }

  private simplifyRxNormDisplay(raw: string): string {
    const s = (raw ?? '').toString().trim();
    if (!s) return '';

    // Prefer [Brand] anywhere (RxNorm often ends with [Brand])
    const brandMatch = s.match(/\[([^\]]+)\]/);
    if (brandMatch?.[1]?.trim()) {
      return brandMatch[1].trim();
    } else {
      return '';
    }

    // If no brand, keep a short readable prefix and drop long form/route tails
    const cutMarkers = [
      ' Oral ',
      ' Injectable ',
      ' Injection ',
      ' Topical ',
      ' Ophthalmic ',
      ' Otic ',
      ' Nasal ',
      ' Inhalant ',
      ' Transdermal ',
      ' Suppository ',
      ' Solution',
      ' Suspension',
      ' Tablet',
      ' Capsule'
    ];
    const markerPos = cutMarkers
      .map(m => s.indexOf(m))
      .filter(i => i > 0)
      .sort((a, b) => a - b)[0];

    const trimmed = markerPos ? s.slice(0, markerPos).trim() : s;
    return trimmed.length > 90 ? `${trimmed.slice(0, 87).trim()}...` : trimmed;
  }

  private searchRxNormSBD(term: string) {
    const url = `https://rxnav.nlm.nih.gov/REST/drugs.json?type=SBD&name=${encodeURIComponent(term)}`;

    const context = new HttpContext()
      .set(LoadingUIEnabled, false)
      .set(DisableCountParam, true)
      .set(DisableSortParam, true);
    //clear cache 
    this.rxNormCachedOptions$.next([]);
    return this.http.get<any>(url, { context }).pipe(
      map((res: any) => {
        const system = 'http://www.nlm.nih.gov/research/umls/rxnorm';
        const groups = res?.drugGroup?.conceptGroup;
        const list = Array.isArray(groups) ? groups : (groups ? [groups] : []);

        const out: Array<{ code: string; display: string; system: string; ingredients: string[] }> = [];

        for (const g of list) {
          const tty = (g?.tty ?? '').toString().toUpperCase();
          if (tty && tty !== 'SBD' && tty !== 'SCD') continue;

          const props = Array.isArray(g?.conceptProperties) ? g.conceptProperties : [];
          for (const cp of props) {
            const code = (cp?.rxcui ?? '').toString().trim();
            const rawDisplay = (cp?.name ?? '').toString().trim();
            if (!code || !rawDisplay) continue;

            if (this.isRxNormPackOrKit(rawDisplay)) continue;

            const display = this.simplifyRxNormDisplay(rawDisplay);
            // if (!display) continue;

            out.push({
              code,
              display,
              system,
              ingredients: this.extractRxNormIngredients(rawDisplay)
            });
          }
        }

        const seen = new Set<string>();
        return out.filter(x => (seen.has(x.code) ? false : (seen.add(x.code), true)));
      })
    );
  }

  private extractRxNormIngredients(rawDisplay: string): string[] {
// $#$    // /* alert(rawDisplay) */
    let ingredients = [...rawDisplay.split('/')];
    ingredients[ingredients.length - 1] = ingredients[ingredients.length - 1].split('[')[0];
    // acetaminophen 300 MG / phenyltoloxamine citrate 20 MG / salicylamide 200 MG Oral Capsule [By Ache]

    return ingredients;
  }

  ngOnInit() {

    this.formMetaData = <formMetaData>{
      formName: 'Prescription Details',
      formDescription: " ",
      submitText: ` ${this.isMultiple.value && this.isMultiple.value == 'Multiple'
        ? 'Add' : 'Submit'}  Medication Request`,

    };


    this.isMultiple.valueChanges.subscribe((e: boolean | null) => {
      if (e !== null) {
        this.isChosenOption = true;
      }
      this.formMetaData = <formMetaData>{
        formName: 'Prescription Details',
        formDescription: " ",
        submitText: ` ${this.isMultiple.value && this.isMultiple.value == 'Multiple'
          ? 'Add' : 'Submit'}  Medication Request`,

      };
    })
    forkJoin({
      medication: this.formFieldsDataService.getFormFieldSelectData('medication', 'medication').pipe(
        timeout(5000),
        catchError(err => {
          // this.loadingRef2.close();
          console.error('Error fetching medication data:', err);
          return of(['Others']);
        })
      ),
      status: this.formFieldsDataService.getFormFieldSelectData('medication', 'status'),
      intent: this.formFieldsDataService.getFormFieldSelectData('medication', 'intent'),
      performerType: this.formFieldsDataService.getFormFieldSelectData('medication', 'performerType'),
      reason: this.formFieldsDataService.getFormFieldSelectData('medication', 'reason'),
      CondInStock: this.http.get<any>(`${this.backendEndPoint}/Condition?patient=${this.stateService.currentEncounter.getValue()?.patientId}&_count=200`),
      MedInStock: this.http.get<any>(`${this.backendEndPoint}/Medication?_count=1000`)

    }).subscribe({
      next: (g: any) => {
        console.log(g.medication);

        // const dRef = this.dialog.open(DynamicFormsV2Component, {
        //   maxHeight: 'calc(90vh - 40px)',
        //   maxWidth: '860px',
        //   autoFocus: false,
        //   data: {


        this.formFields = <FormFields[]>[
          // {
          //   generalProperties: {
          //     fieldApiName: 'status',
          //     fieldName: 'Status of Medication',
          //     fieldLabel: 'Status of Medication',
          //     value: 'active',
          //     auth: {
          //       read: 'all',
          //       write: 'doctor, nurse'
          //     },
          //     fieldType: 'SingleCodeField',
          //     isArray: false,
          //     isGroup: false
          //   },
          //   data: g.status
          // },
          {
            generalProperties: {
              fieldApiName: 'intent',
              fieldName: 'Intent of Medication',
              fieldLabel: 'Intent of Medication',
              value: 'order',
              moreHint: "Do you intend that this medication should be ordered right away or is just a proposal",
              auth: {
                read: 'all',
                write: 'doctor, nurse'
              },
              validations: [
                {
                  type: 'default',
                  name: 'required',
                }
              ],
              fieldType: 'SingleCodeField',
              isArray: false,
              isGroup: false
            },
            // data: g.intent
            data: ['plan', 'proposal', 'order', 'option']
          },
          {
            generalProperties: {
              fieldApiName: 'priority',
              fieldName: 'Priority ',
              fieldLabel: 'Priority ',
              auth: {
                read: 'all',
                write: 'doctor, nurse'
              },
              fieldType: 'SingleCodeField',
              isArray: false,
              isGroup: false
            },
            data: "routine | urgent | asap | stat".split(" | ")
          },
          <GroupField>{
            //medication field
            generalProperties: {
              fieldApiName: 'medicationRequestMedicationGroupField',
              fieldLabel: 'Medicine',
              fieldName: 'Medicine',
              isGroup: true,
              fieldType: 'IndividualField',
              isArray: false,
              isHidden: true,
              groupFieldsHint: "Select the medicine to be prescribed. You can choose from existing medications or enter a new one.",
            },

            keys: ['medicationRequestMedicine'],
            groupFields: {
              'medicationRequestMedicine': {
                generalProperties:
                {
                  fieldApiName: 'medicationRequestMedicine',
                  fieldName: 'Actual Medicine',
                  fieldLabel: 'Actual Medicine',
                  fieldHint: "Actual medicine to be prescribed",

                  auth: {
                    read: 'all',
                    write: 'doctor, nurse'
                  },
                  allowedOthers: true,
                  fieldType: 'IndividualField',
                  isArray: false,
                  isGroup: false
                },
                data: g.MedInStock && g.MedInStock.entry
                  ? this.utilityService.filterAvailableMedications(g.MedInStock.entry).map((e: any) => {

                    return {
                      reference: 'Medication/' + e.resource.id,
                      display: `${e.resource.code?.text || e.resource.code?.coding?.[0]?.display}`,
                      ingredients: this.utilityService.extractMedicationIngredients(e.resource),
                      inStock: true
                    };
                  })
                  : []

              }
            }
          },



          {
            generalProperties: {
              fieldApiName: 'performerType',
              fieldName: 'To Be Administered By',
              fieldLabel: 'To Be Administered By',
              fieldHint: "Who should administer the medication to the patient",
              auth: {
                read: 'all',
                write: 'doctor, nurse'
              },
              allowedOthers: true,
              fieldType: 'CodeableConceptField',
              isArray: false,
              isGroup: false
            },
            data: g.performerType
          },
          <GroupField>{
            generalProperties: {
              fieldApiName: 'reason',
              fieldLabel: 'Reason for Medication',
              allowedOthers: true,
              fieldName: 'Reason for Medication',
              moreHint: "Specify the reason for this medication. You can select from patient's existing conditions or enter a new reason.",
              fieldHint: "Choose whether to select from patient conditions or enter a new reason",
              groupFieldsHint: "Specify the reason for this medication. The reason could be a patient's existing conditions or a new reason code",
              auth: {
                read: 'all',
                write: 'doctor, nurse'
              },
              fieldType: 'IndividualField',
              isArray: true,
              isGroup: true
            },
            keys: ['reasonSource', 'reasonReference', 'reasonCode'],
            groupFields: {
              ...(g.CondInStock?.entry?.some((e: any) =>
                e.resource.asserter?.reference !== `Patient/${this.stateService.currentEncounter.getValue()?.patientId}` &&
                (e.resource.code?.text || e.resource.code?.coding?.[0]?.display) //&&
                // e.resource.encounter?.reference === `Encounter/${this.stateService.currentEncounter.getValue()?.['id']}`
              ) ? {
                'reasonSource': {
                  generalProperties: {
                    fieldApiName: 'reasonSource',
                    fieldName: 'Reason Source',
                    fieldLabel: 'Where is the reason coming from?',
                    moreHint: "Select whether to choose from patient's existing conditions or enter a new reason",
                    fieldHint: "Choose 'From Patient Condition' to select from existing diagnoses, or 'New Reason' to enter a new one",
                    auth: {
                      read: 'all',
                      write: 'doctor, nurse'
                    },
                    fieldType: 'SingleCodeField',
                    isArray: false,
                    isGroup: false,
                    inputType: 'select',
                    controllingField: [
                      {
                        isAControlField: true,
                        dependentFieldVisibilityTriggerValue: 'From Patient Condition',
                        controlledFieldDependencyId: 'reasonReference'
                      },
                      {
                        isAControlField: true,
                        dependentFieldVisibilityTriggerValue: 'New Reason',
                        controlledFieldDependencyId: 'reasonCode'
                      }
                    ]
                  },
                  data: [
                    'From Patient Condition',
                    'New Reason'
                  ]
                },

                'reasonReference': {
                  generalProperties: {
                    fieldApiName: 'reasonReference',
                    fieldName: 'Reason from Patient Conditions',
                    fieldLabel: 'Reason from Patient Conditions',
                    moreHint: "Select reason from patient's existing conditions",
                    fieldHint: "Choose from patient's diagnosed conditions",
                    auth: {
                      read: 'all',
                      write: 'doctor, nurse'
                    },
                    fieldType: 'IndividualReferenceField',
                    isArray: false,
                    isGroup: false,
                    dependence_id: 'reasonReference'
                  },
                  data: g.CondInStock?.entry
                    ?.filter((e: any) =>
                      e.resource.asserter?.reference !== `Patient/${this.stateService.currentEncounter.getValue()?.patientId}` //&&
                      // e.resource.encounter?.reference === `Encounter/${this.stateService.currentEncounter.getValue()?.['id']}`
                    )
                    .map((e: any) => ({
                      reference: `Condition/${e.resource.id}`,
                      display: e.resource.code?.text || e.resource.code?.coding?.[0]?.display || 'Unknown diagnosis'
                    })) || []
                },
                'reasonCode': {
                  generalProperties: {
                    fieldApiName: 'reasonCode',
                    fieldName: 'New Reason for Medication',
                    fieldLabel: 'New Reason for Medication',
                    moreHint: "Enter a new reason that is not in patient's existing conditions",
                    fieldHint: "Search and select or enter a new reason code",
                    auth: {
                      read: 'all',
                      write: 'doctor, nurse'
                    },
                    fieldType: 'CodeableConceptFieldFromBackEnd',
                    isArray: false,
                    isGroup: false,
                    allowedOthers: true,
                    dependence_id: 'reasonCode'
                  },
                  data: g.reason
                }
              } : {
                'reasonCode': {
                  generalProperties: {
                    fieldApiName: 'reasonCode',
                    fieldName: 'New Reason for Medication',
                    fieldLabel: 'New Reason for Medication',
                    moreHint: "Enter a new reason that is not in patient's existing conditions",
                    fieldHint: "Search and select or enter a new reason code",
                    auth: {
                      read: 'all',
                      write: 'doctor, nurse'
                    },
                    fieldType: 'CodeableConceptFieldFromBackEnd',
                    isArray: false,
                    isGroup: false,
                    allowedOthers: true,
                    // dependence_id: 'reasonCode'
                  },
                  data: g.reason
                }
              }),
            }
          },
          {
            generalProperties: {
              fieldApiName: 'dosageInstruction',
              fieldName: 'Dosage Instruction',
              fieldLabel: 'Dosage Instruction',
              validations: [
                {
                  type: 'default',
                  name: 'required',

                }
              ],
              fieldHint: "Provide detailed dosage instructions for the medication",
              auth: {
                read: 'all',
                write: 'doctor, nurse'
              },
              inputType: "textarea",
              isArray: false,
              isGroup: false
            },
            data: g.performerType
          }
        ];

        // Prepare stock options once (used also by ICD-11 merged autocomplete)
        const stockEntries = (g.MedInStock?.entry && Array.isArray(g.MedInStock.entry)) ? g.MedInStock.entry : [];
        const available = this.utilityService.filterAvailableMedications(stockEntries);

        this.stockMedicationOptions$.next(
          available.map((e: any) => {
            const stock = this.utilityService.getMedicationTotalRemaining(e.resource);
            const displayBase = e.resource.code?.text || e.resource.code?.coding?.[0]?.display || 'Unnamed medication';
            return {
              reference: `Medication/${e.resource.id}`,
              display: `${displayBase}`, //(Stock: ${stock?.value || 0} ${stock?.unit || 'units'})`,
              inStock: true,
              ingredients: this.utilityService.extractMedicationIngredients(e.resource)
            };
          })
        );

        this.formFields = [...this.formFields, ...this.utilityService.commonFormFields['dosage'] as FormFields[]];

        if (g.CondInStock && g.CondInStock.entry && g.CondInStock.entry.length > 0
          && g.CondInStock.entry.some((e: any) => e.resource.asserter?.reference !== `Patient/${this.stateService.currentEncounter.getValue()?.patientId}
            && (e.resource.code?.text || e.resource.code?.coding?.[0]?.display) &&
            e.encounter?.reference === Encounter/${this.stateService.currentEncounter.getValue()?.['id']}
            `)

        ) {
          // if (res.entry && res.entry.length > 0) {
          // this.formFields?.splice(4, 0, {

          //   generalProperties: {
          //     fieldApiName: 'reasonReference',
          //     fieldName: 'Medication Reason from Patient Conditions',
          //     fieldLabel: 'Medication Reason from Patient Conditions',
          //     moreHint: "Select reason for the medication from patient's existing conditions",
          //     fieldHint: "If the reason for medication is from patient's existing conditions, select here",
          //     auth: {
          //       read: 'all',
          //       write: 'doctor, nurse'
          //     },
          //     fieldType: 'IndividualReferenceField',
          //     isArray: true,
          //     isGroup: false
          //   },
          //   data: g.CondInStock.entry.filter((e: any) => e.resource.asserter?.reference !== `Patient/${this.stateService.currentEncounter.getValue()?.patientId}`)
          //     .map((e: any) => { return { reference: e.resource.id, display: e.resource.code?.text || e.resource.code?.coding?.[0]?.display || "unknown diagnosis" } })
          // });


        }

        if (g.MedInStock && g.MedInStock.entry && g.MedInStock.entry.length > 0) {
          // if (res.entry && res.entry.length > 0) {
          // {
          //   console.log('MedInStock', g.MedInStock);
          //   // if (res.entry && res.entry.length > 0) {
          //   this.formFields?.splice(3, 0, {

          //     generalProperties: {
          //       fieldApiName: 'medicationReference',
          //       fieldName: 'Medication from Stock',
          //       fieldLabel: 'Medication from Stock',
          //       moreHint: "Select medication from Pharmacy stock",
          //       fieldHint: "If the medicine is available in Pharmacy stock, select here",
          //       auth: {
          //         read: 'all',
          //         write: 'doctor, nurse'
          //       },
          //       fieldType: 'IndividualReferenceField',
          //       isArray: false,
          //       isGroup: false
          //     },
          //     data: g.MedInStock.entry.map((e: any) => { return { reference: 'Medication/' + e.resource.id, display: e.resource.code?.text || e.resource.code?.coding?.[0]?.display } })
          //   }
          //   );


          // }

        }




      },

      // this.medicineForms.push({ formMetaData: this.formMetaData, formFields: [...this.formFields] });





      error: (err: any) => {
        console.error('Error fetching medication data:', err);
        this.errorService.openandCloseError('Error fetching medication data. Please try again later.');
      }
    });
  }
  addMoreMedicineRequest() {
    if (this.formMetaData && this.formFields) {
      this.medicineForms = [...this.medicineForms, { formMetaData: this.formMetaData, formFields: [...this.formFields] }];
    }
  }

  mapIntentToStatus = new Map<string, string>([
    ['proposal', 'draft'],
    ['plan', 'draft'],
    ['order', 'active'],
    ['option', 'active']
  ]);

  private buildMedicationRequestFromForm(values: any): MedicationRequest | null {
    if (!values) return null;

    const patientId = this.stateService.currentPatientId$.getValue();
    const encounterId = this.stateService.currentEncounter.getValue()?.['id'];

    const medicationPick = values?.medicationRequestMedicationGroupField?.medicationRequestMedicine;

    // medication
    const medicationPart = this.mapMedicationSelectionToMedicationRequest(medicationPick);
    if (!medicationPart) return null;
    //no patietid? error
    if (!patientId) {
      this.errorService.openandCloseError('No patient selected. Cannot create MedicationRequest without a patient.');
    }

    // subject
    const subject: Reference = { reference: `Patient/${patientId}` };

    // intent/status/priority
    const intent = (values?.intent ?? '').toString().trim() as MedicationRequest['intent'];
    const priority = (values?.priority ?? '').toString().trim() as MedicationRequest['priority'];
    const status = this.mapIntentToStatus.get(intent) as MedicationRequest['status'] | undefined;

    // performerType -> requester (best-effort; performerType is not a perfect mapping)
    // const requester = this.mapPerformerTypeToRequester(values?.performerType);

    // reason (array)
    const { reasonReference, reasonCode } = this.mapReasons(values?.reason);

    // dosage
    const dosageInstruction = this.utilityService.constructDosageTypeObject(values) ? [this.utilityService.constructDosageTypeObject(values)!] : [];

    const mr: MedicationRequest = {
      resourceType: 'MedicationRequest',
      intent: (intent || 'order') as MedicationRequest['intent'],
      status: (status || 'active') as MedicationRequest['status'],
      ...(priority ? { priority: priority as MedicationRequest['priority'] } : {}),
      subject,
      ...(encounterId ? { encounter: { reference: `Encounter/${encounterId}` } } : {}),
      // ...(requester ? { requester } : {}),
      ...medicationPart,
      ...(reasonReference.length ? { reasonReference } : {}),
      ...(reasonCode.length ? { reasonCode } : {}),
      ...(dosageInstruction.length ? { dosageInstruction } : {})
    };

    return mr;
  }

  private mapMedicationSelectionToMedicationRequest(
    pick: any
  ): Pick<MedicationRequest, 'medicationReference' | 'medicationCodeableConcept'> | null {
    if (!pick || typeof pick !== 'object') {
      if (String(pick).trim() !== '') {
        return {
          medicationCodeableConcept: {
            text: String(pick)
          }
        };
      } else {
        return null;
      }

    }


    if (pick.kind === 'stock' && pick.reference) {
      return {
        medicationReference: {
          reference: String(pick.reference),
          display: pick.display || pick.label || undefined
        }
      };
    }

    if (pick.kind === 'rxnorm' && pick.code && pick.system) {
      return {
        medicationCodeableConcept: {
          coding: [{
            system: String(pick.system),
            code: String(pick.code),
            display: pick.display || undefined
          }],
          text: pick.display || undefined
        }
      };
    }

    if (pick.kind === 'userExactWords' && pick.display) {
      return {
        medicationCodeableConcept: {
          text: String(pick.display)
        }
      };
    }

    return null;
  }

  private mapPerformerTypeToRequester(raw: any): Reference | undefined {
    // Your form holds performerType as a string or "$#$" value or object depending on the field type.
    // Keep it conservative: if it already resembles a FHIR reference, use it; else skip.
    if (!raw) return undefined;

    if (typeof raw === 'string') {
      // allow "$#$" encoded refs (Reference fields do that elsewhere in the app)
      const s = raw.trim();
      const ref = s.includes('$#$') ? s.split('$#$')[0].trim() : s;
      if (ref.includes('/')) return { reference: ref };
      return undefined;
    }

    if (typeof raw === 'object' && raw.reference) {
      return { reference: raw.reference, display: raw.display };
    }

    return undefined;
  }

  private mapReasons(raw: any): { reasonReference: Reference[]; reasonCode: CodeableConcept[] } {
    const reasonReference: Reference[] = [];
    const reasonCode: CodeableConcept[] = [];

    const rows = Array.isArray(raw) ? raw : [];
    for (const r of rows) {
      // reasonReference (Condition/..)
      const rr = r?.reasonReference;
      if (rr) {
        if (typeof rr === 'string') {
          const ref = rr.includes('$#$') ? rr.split('$#$')[0].trim() : rr.trim();
          if (ref) reasonReference.push({ reference: ref });
        } else if (typeof rr === 'object' && rr.reference) {
          reasonReference.push({ reference: rr.reference, display: rr.display });
        }
      }

      // reasonCode (CodeableConceptFieldFromBackEnd is usually "$#$" encoded)
      const rc = r?.reasonCode;
      if (rc) {
        if (typeof rc === 'string') {
          const s = rc.trim();
          if (s.includes('$#$')) {
            const [code = '', display = '', system = ''] = s.split('$#$');
            reasonCode.push({
              coding: [{ code, display, system }],
              text: display || code
            });
          } else {
            reasonCode.push({ text: s });
          }
        } else if (typeof rc === 'object') {
          // tolerate {code, display, system}
          if (rc.code || rc.display) {
            reasonCode.push({
              coding: [{
                code: String(rc.code ?? ''),
                display: String(rc.display ?? ''),
                system: String(rc.system ?? '')
              }],
              text: String(rc.display ?? rc.code ?? '')
            });
          } else if (rc.text) {
            reasonCode.push({ text: String(rc.text) });
          }
        }
      }
    }

    return { reasonReference, reasonCode };
  }

  private mapDosage(values: any): Dosage[] {
    const out: Dosage[] = [];

    const text = (values?.dosageInstruction ?? '').toString().trim();
    const timingCode = values?.dosageTiming?.timingCode;
    const duration = values?.dosageTiming?.duration;
    const durationUnit = values?.dosageTiming?.durationUnit;

    const qtyVal = values?.doseAndRateDoseQuantity?.value;
    const qtyUnit = values?.doseAndRateDoseQuantity?.unit;

    const dosage: Dosage = {};

    if (text) {
      dosage.text = text;
    }

    // timing
    const timingText = typeof timingCode === 'string'
      ? timingCode
      : (timingCode?.display || timingCode?.text || '');

    const durNum = duration !== '' && duration != null && !Number.isNaN(Number(duration))
      ? Number(duration)
      : undefined;

    const durUnit = (durationUnit ?? '').toString().trim() || undefined;

    if (timingText || durNum || durUnit) {
      dosage.timing = {
        ...(timingText ? { code: { text: timingText } } : {}),
        ...(durNum || durUnit ? {
          repeat: {
            ...(durNum ? { duration: durNum } : {}),
            ...(durUnit ? { durationUnit: durUnit as any } : {})
          }
        } : {})
      } as any;
    }

    // dose quantity
    const doseNum = qtyVal !== '' && qtyVal != null && !Number.isNaN(Number(qtyVal)) ? Number(qtyVal) : undefined;
    const doseUnit = (qtyUnit ?? '').toString().trim() || undefined;

    if (doseNum || doseUnit) {
      dosage.doseAndRate = [{
        doseQuantity: {
          ...(doseNum != null ? { value: doseNum } : {}),
          ...(doseUnit ? { unit: doseUnit } : {})
        }
      }];
    }

    if (Object.keys(dosage).length) out.push(dosage);
    return out;
  }
  /**
   * Trigger notifications for newly created medication requests
   */
  private triggerMedicationRequestNotifications(medicationRequests: MedicationRequest[]): void {
    const organizationId = this.getCurrentOrganizationId();

    if (!organizationId) {
      console.warn('No organization ID found, skipping notifications');
      return;
    }

    const patientId = this.stateService.currentPatientId$.getValue();

    // Get patient name for notification message
    this.utilityService.getPatientName(patientId).pipe(
      take(1)
    ).subscribe({
      next: (patientName) => {
        medicationRequests.forEach((mr) => {
          if (!mr.id) {
            console.warn('MedicationRequest has no ID, skipping notification');
            return;
          }

          // Extract medication display name
          const medicationDisplay =
            mr.medicationReference?.display ||
            mr.medicationCodeableConcept?.text ||
            mr.medicationCodeableConcept?.coding?.[0]?.display ||
            'Unknown medication';

          // Determine urgency based on priority
          const urgency = mr.priority === 'stat' || mr.priority === 'urgent' ? 'high' : 'normal';

          this.notificationService.triggerNotification({
            resourceType: 'MedicationRequest',
            resourceId: mr.id,
            organizationId: organizationId,
            targetRoles: ['pharmacist', 'pharmacy'], // Notify pharmacy staff
            title: 'New Prescription Available',
            message: `${patientName || 'Patient'} has a new prescription for ${medicationDisplay}`,
            sendEmail: urgency === 'high', // Send email for urgent prescriptions
            data: {
              patientId: patientId || '',
              medicationDisplay: medicationDisplay,
              urgency: urgency,
              priority: mr.priority || 'routine',
              intent: mr.intent
            }
          }).subscribe({
            next: () => console.log(`✅ Notification sent for MedicationRequest ${mr.id}`),
            error: (err) => console.error(`❌ Failed to send notification for MedicationRequest ${mr.id}`, err)
          });

          // ✅ Post to Firebase Realtime Database
          this.firebaseAlertService.postRoleAlert(organizationId, 'pharmacy', {
            title: 'New Prescription Available',
            description: `${patientName || 'Patient'} has a new prescription for ${medicationDisplay}`,
            url: `/medicine-requests`, // Adjust URL as needed
            query: { patientId: patientId }
          }).subscribe();
        });
      },
      error: (err) => {
        console.error('Failed to get patient name for notification', err);
        // Still send notifications without patient name
        this.sendNotificationsWithoutPatientName(medicationRequests, organizationId, patientId);
      }
    });
  }
  /**
   * Fallback method to send notifications without patient name
   */
  private sendNotificationsWithoutPatientName(
    medicationRequests: MedicationRequest[],
    organizationId: string,
    patientId: string | null
  ): void {
    medicationRequests.forEach((mr) => {
      if (!mr.id) return;

      const medicationDisplay =
        mr.medicationReference?.display ||
        mr.medicationCodeableConcept?.text ||
        mr.medicationCodeableConcept?.coding?.[0]?.display ||
        'Unknown medication';

      const urgency = mr.priority === 'stat' || mr.priority === 'urgent' ? 'high' : 'normal';

      this.notificationService.triggerNotification({
        resourceType: 'MedicationRequest',
        resourceId: mr.id,
        organizationId: organizationId,
        targetRoles: ['pharmacist', 'pharmacy'],
        title: 'New Prescription Available',
        message: `New prescription for ${medicationDisplay}`,
        sendEmail: urgency === 'high',
        data: {
          patientId: patientId || '',
          medicationDisplay: medicationDisplay,
          urgency: urgency,
          priority: mr.priority || 'routine',
          intent: mr.intent
        }
      }).subscribe({
        next: () => console.log(`✅ Notification sent for MedicationRequest ${mr.id}`),
        error: (err) => console.error(`❌ Failed to send notification for MedicationRequest ${mr.id}`, err)
      });
    });
  }
  processValues(values: any) {
    const errorMessages = []
    if (!values) return;

    // Validate required fields
    if (
      values.intent == '' || !values.intent
    ) {
      errorMessages.push(' Intent field is required.');
      // return;
    }

    //check rxNormMedicationCtrl 

    if (!this.rxNormMedicationCtrl.value) {
      errorMessages.push(' Medication field is required.');
    }

    if (errorMessages.length > 0) {
      this.errorService.openandCloseError('Please address the following errors before submitting: ' + errorMessages.join(' '));
      return;
    }

    //add medicatinReference or mediatinCodeableConcept based on rxNormMedicationCtrl value
    if (this.rxNormMedicationCtrl.value && (!this.isMultiple.value || this.isMultiple.value == 'Single')) {
      const medicationnCtrlValue = this.rxNormMedicationCtrl.value;
      // values = {
      //   ...values, ...(medicationnCtrlValue.kind === 'stock' ? {
      //     medicationReference: {
      //       reference: medicationnCtrlValue.reference,
      //       display: medicationnCtrlValue.label
      //     }
      //   }
      //     : medicationnCtrlValue.kind === 'rxnorm' ? {
      //       medicationCodeableConcept: <MedicationRequest['medicationCodeableConcept']>{
      //         coding: [
      //           {
      //             code: medicationnCtrlValue.code,
      //             system: medicationnCtrlValue.system,
      //             display: medicationnCtrlValue.label
      //           }
      //         ],
      //         text: medicationnCtrlValue.label,
      //       }
      //     } : <MedicationRequest['medicationCodeableConcept']>{
      //       text: medicationnCtrlValue.display

      //     })
      // };
    }
    values.medicationRequestMedicationGroupField.medicationRequestMedicine = this.rxNormMedicationCtrl.value;
    let rep = (this.formFields?.find(f => f.generalProperties.fieldApiName === 'medicationRequestMedicationGroupField') as GroupField | undefined)?.
      groupFields?.['medicationRequestMedicine']?.
      data
    if (rep) {
      rep = this.stockMedicationOptions$.getValue();
    }

    // Set status based on intent
    values.status = this.mapIntentToStatus.get(values.intent) || 'active';
// $#$    // /* alert(JSON.stringify(values) */);
    if (this.rxNormMedicationCtrl.value) {
      if (!this.isMultiple.value || this.isMultiple.value == 'Single') {
        const medicationRequest = this.buildMedicationRequestFromForm(values);

        if (this.dialogRef) {
// $#$          // /* alert(JSON.stringify(medicationRequest) */);
          this.dialogRef.close({ values: medicationRequest });

          // ✅ Trigger notification for single medication request
          if (medicationRequest) {
            this.triggerMedicationRequestNotifications([medicationRequest]);
            // Request UI lists to refresh (medicine requests list) — full refresh for creates
            // try { this.resourceRefresh.notify('medicine-requests', { full: true }); } catch (e) { }
          }
        }
        return;
      }

      this.items.push(this.buildItemGroup(values));

      // Clear the form after adding to the group


      setTimeout(() => {
        document.getElementById('review-item-' + (this.items.length - 1))?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 200);
    }
    // this.toSubmitFinally = this.items.value;
  }

  scrollToForm() {
    setTimeout(() => {
      document.getElementById('medication-form-top')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }

  submitAllRequests() {
    const allValues = this.items.value;
    const fhir = (Array.isArray(allValues) ? allValues : [])
      .map(v => this.buildMedicationRequestFromForm(v))
      .filter(Boolean) as MedicationRequest[];

    if (this.dialogRef) {
      this.dialogRef.close({ values: fhir });
      this.triggerMedicationRequestNotifications(fhir);
      // try { this.resourceRefresh.notify('medicine-requests', { full: true }); } catch (e) { }

    }
  }

  // private dialogData = inject<AddMedicineRequestsDialogData | null>(MAT_DIALOG_DATA, { optional: true });

  // isEditMode = !!this.dialogData?.isEditMode;

  // This is what ResourceDataReview edits (dynamic-forms-like shape)
  submittedData: any = {};

  onEditField(evt: { fieldApiName: string; newValue: any }) {
    if (!evt?.fieldApiName) return;
    this.submittedData = this.submittedData ?? {};
    this.submittedData[evt.fieldApiName] = evt.newValue;
    this.toSubmitFinally = this.submittedData;
  }

  onEditResource(updated: any) {
    if (!updated) return;
    this.submittedData = { ...(updated ?? {}) };
    this.toSubmitFinally = this.submittedData;
  }

  submitEdit() {
    const fhir = this.buildMedicationRequestFromForm(this.submittedData);
    if (!fhir) {
      this.errorService.openandCloseError('Unable to build MedicationRequest from edited values.');
      return;
    }
    this.dialogRef?.close({ values: fhir });

  }

  private medicationRequestToDynamicFormValues(mr: MedicationRequest): any {
    // Matches your current expected dynamic-forms output:
    // { intent, priority, medicationRequestMedicationGroupField: { medicationRequestMedicine: <pick> }, performerType, reason: [...], dosageTiming, doseAndRateDoseQuantity, dosageInstruction }
    const intent = (mr.intent ?? 'order') as any;
    const priority = (mr.priority ?? '') as any;

    const medicationPick = this.medicationRequestToMedicinePick(mr);

    const reasonArray = this.medicationRequestToReasonArray(mr);

    const dosage = Array.isArray(mr.dosageInstruction) ? mr.dosageInstruction[0] : undefined;
    const dosageText = (dosage?.text ?? '') as any;

    const timingCode =
      dosage?.timing?.code?.coding?.[0]
        ? dosage.timing.code.coding[0] // keep object (your UtilityService supports timingCode as object or string)
        : (dosage?.timing?.code?.text ?? '');

    const duration = dosage?.timing?.repeat?.duration ?? '';
    const durationUnit = dosage?.timing?.repeat?.durationUnit ?? '';

    const doseQty = dosage?.doseAndRate?.[0]?.doseQuantity;
    const doseValue = doseQty?.value ?? '';
    const doseUnit = doseQty?.unit ?? '';

    return {
      intent,
      priority,
      medicationRequestMedicationGroupField: {
        medicationRequestMedicine: medicationPick
      },
      performerType: '',
      reason: reasonArray,
      dosageInstruction: dosageText,
      dosageTiming: {
        timingCode,
        duration: duration === undefined || duration === null ? '' : String(duration),
        durationUnit: durationUnit ?? ''
      },
      doseAndRateDoseQuantity: {
        value: doseValue === undefined || doseValue === null ? '' : String(doseValue),
        unit: doseUnit ?? ''
      }
    };
  }

  private medicationRequestToMedicinePick(mr: MedicationRequest): any {
    // Convert FHIR medication[x] back to your selection object so ResourceDataReview special-case can re-edit it.
    if (mr.medicationReference?.reference) {
      const display = mr.medicationReference.display ?? '';
      return {
        kind: 'stock',
        reference: mr.medicationReference.reference,
        label: display || mr.medicationReference.reference,
        display: display || mr.medicationReference.reference,
        inStock: true
      };
    }

    const coding = mr.medicationCodeableConcept?.coding?.[0];
    if (coding?.code && coding?.system) {
      const display = coding.display ?? mr.medicationCodeableConcept?.text ?? coding.code;
      return {
        kind: 'rxnorm',
        code: coding.code,
        system: coding.system,
        display,
        label: display,
        ingredients: []
      };
    }

    const text = mr.medicationCodeableConcept?.text;
    if (text) {
      return {
        kind: 'userExactWords',
        display: text,
        label: `Use "${text}"`,
        system: 'user/input'
      };
    }

    return null;
  }

  private medicationRequestToReasonArray(mr: MedicationRequest): any[] {
    const refs = Array.isArray(mr.reasonReference) ? mr.reasonReference : [];
    const codes = Array.isArray(mr.reasonCode) ? mr.reasonCode : [];

    const max = Math.max(refs.length, codes.length, 1);
    const out: any[] = [];

    for (let i = 0; i < max; i++) {
      const rr = refs[i];
      const rc = codes[i];

      out.push({
        reasonSource: '',
        reasonReference: rr?.reference ? { reference: rr.reference, display: rr.display } : '',
        reasonCode: rc ? this.codeableConceptToCoding(rc) : ''
      });
    }

    return out;
  }

  private codeableConceptToCoding(cc: CodeableConcept): { code: string; display: string; system: string } | string | null {
    const c = cc?.coding?.[0];
    if (c?.code || c?.display || c?.system) {
      const code = (c.code ?? '').toString();
      const display = (c.display ?? cc.text ?? c.code ?? '').toString();
      const system = (c.system ?? '').toString();
      return { code, display, system };
    }
    return (cc?.text ?? '').toString();
  }

  /* Removed incorrect local withLatestFrom function. Use the import from 'rxjs' instead. */
}

